import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { RoleBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { GlassField, GlassButton } from "@/components/GlassBits";
import { PasskeyCard } from "@/components/admin/PasskeyCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Check, Copy, History, KeyRound, ShieldPlus, Sparkles, Trash2 } from "lucide-react";

const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";

/** Genera el código EN TU NAVEGADOR (no viaja a ningún sitio hasta que lo guardas). */
const generateCode = (length = 20) => {
  const bytes = new Uint32Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]).join("");
};

const err = (e, fallback = "Error") => e.response?.data?.detail || fallback;

const METHOD_LABEL = {
  codigo: "Código único",
  telegram: "Código por Telegram",
  huella: "Huella o cara",
};

export default function Admins() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tid, setTid] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [role, setRole] = useState("ADMIN");
  const [editing, setEditing] = useState(null);
  const [newCode, setNewCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [copied, setCopied] = useState(null);
  const isSuper = user?.role === "SUPER_ADMIN";

  const { data: admins = [] } = useQuery({
    queryKey: ["admins"],
    queryFn: async () => (await api.get("/admin/admins")).data,
  });
  const reload = () => qc.invalidateQueries({ queryKey: ["admins"] });

  const { data: logins = { logins: [], failed_24h: 0 } } = useQuery({
    queryKey: ["admin-logins", onlyFailed],
    queryFn: async () =>
      (await api.get("/admin/admin-logins", { params: { limit: 100, only_failed: onlyFailed } })).data,
    enabled: isSuper,
    refetchInterval: 30000,
  });

  const invite = async () => {
    if (!tid.trim()) return toast.error("Escribe el Telegram ID del nuevo administrador");
    if (code.trim().length < 8) return toast.error("El código debe tener al menos 8 caracteres");
    setBusy(true);
    try {
      await api.post("/admin/admins/invite", {
        telegram_id: tid.trim(), role, code: code.trim(), name: name.trim(),
      });
      toast.success("Administrador dado de alta. Entrégale su código en privado.");
      setTid(""); setName(""); setCode("");
      reload();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  const copyCode = async (value, which) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      setTimeout(() => setCopied(null), 2500);
      toast.success("Código copiado. Guárdalo en un lugar seguro.");
    } catch (e) {
      toast.error("No se pudo copiar: selecciónalo y cópialo a mano");
    }
  };

  const saveCode = async (telegram_id) => {
    if (newCode.trim().length < 8) return toast.error("El código debe tener al menos 8 caracteres");
    try {
      await api.post(`/admin/admins/${telegram_id}/code`, { code: newCode.trim() });
      toast.success("Código actualizado");
      setEditing(null); setNewCode("");
      reload();
    } catch (e) { toast.error(err(e)); }
  };

  const removeCode = async (telegram_id) => {
    try {
      await api.delete(`/admin/admins/${telegram_id}/code`);
      toast.success("Código eliminado: ya no puede entrar con él");
      reload();
    } catch (e) { toast.error(err(e)); }
  };

  const setUserRole = async (telegram_id, newRole) => {
    try {
      await api.post("/admin/admins/role", { telegram_id, role: newRole });
      toast.success("Rol actualizado");
      reload();
    } catch (e) { toast.error(err(e)); }
  };

  return (
    <Layout>
      <PageHeader title="Administradores"
        subtitle="Da de alta administradores y entrégales su código único de acceso" />
      <div className="p-8 space-y-6">
        <PasskeyCard />
        {isSuper && (
          <div className="glass-card p-6 max-w-2xl" data-testid="admin-invite-card">
            <div className="flex items-center gap-2 mb-5 text-white font-heading font-semibold">
              <ShieldPlus className="w-5 h-5 text-red-400" /> Nuevo administrador
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <GlassField data-testid="admin-tid-input" label="Telegram ID" value={tid}
                inputClass="font-mono" placeholder="Ej: 6731591552"
                onChange={(e) => setTid(e.target.value.replace(/\D/g, ""))} />
              <GlassField data-testid="admin-name-input" label="Nombre (opcional)" value={name}
                placeholder="Ej: Laura" onChange={(e) => setName(e.target.value)} />
              <div>
                <label className="block text-xs text-white/55 mb-1.5">Código único de acceso</label>
                <div className="flex items-center gap-2">
                  <input data-testid="admin-code-new-input" value={code}
                    placeholder="Mínimo 8 caracteres" onChange={(e) => setCode(e.target.value)}
                    className="glass-field flex-1 px-3 py-2.5 rounded-xl text-sm text-white font-mono placeholder-white/35 outline-none" />
                  <button type="button" data-testid="generate-code-btn" title="Generar código seguro"
                    onClick={() => setCode(generateCode())}
                    className="glass-btn w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4" />
                  </button>
                  <button type="button" data-testid="copy-code-btn" title="Copiar código"
                    disabled={!code} onClick={() => copyCode(code, "new")}
                    className="glass-btn w-10 h-10 rounded-xl flex items-center justify-center shrink-0 disabled:opacity-40">
                    {copied === "new" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-white/55 mb-1.5">Rol</label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="admin-role-select"
                    className="glass-field h-[42px] text-white border-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0D0407] border-red-500/25 text-white">
                    <SelectItem value="ADMIN">ADMIN</SelectItem>
                    <SelectItem value="SUPER_ADMIN">SUPER_ADMIN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <GlassButton data-testid="admin-invite-btn" tone="red" className="w-auto px-5"
                loading={busy} onClick={invite}>Dar de alta</GlassButton>
              <p className="text-[11px] text-white/45">
                Pulsa ✨ para generar un código seguro de 20 caracteres: se crea en TU navegador, cópialo
                y guárdalo porque no se puede volver a ver (se almacena cifrado con bcrypt).
              </p>
            </div>
          </div>
        )}

        <div className="glass-card overflow-hidden max-w-4xl">
          <table className="w-full text-sm" data-testid="admins-table">
            <thead>
              <tr className="text-left text-xs text-white/45 uppercase border-b border-red-500/15">
                <th className="px-4 py-3">Telegram ID</th>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Rol</th>
                <th className="px-4 py-3">Código</th>
                {isSuper && <th className="px-4 py-3"></th>}
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.telegram_id} className="border-t border-white/5">
                  <td className="px-4 py-3 font-mono text-white/90">{a.telegram_id}</td>
                  <td className="px-4 py-3 text-white/60">{a.name || a.username || "—"}</td>
                  <td className="px-4 py-3"><RoleBadge role={a.role} /></td>
                  <td className="px-4 py-3" data-testid={`admin-code-state-${a.telegram_id}`}>
                    {a.has_code
                      ? <span className="text-emerald-400 text-xs">Activo</span>
                      : <span className="text-white/40 text-xs">Sin código</span>}
                  </td>
                  {isSuper && (
                    <td className="px-4 py-3">
                      {editing === a.telegram_id ? (
                        <div className="flex items-center gap-2">
                          <input value={newCode} onChange={(e) => setNewCode(e.target.value)}
                            data-testid={`admin-code-field-${a.telegram_id}`} autoFocus
                            placeholder="Código nuevo"
                            className="glass-field px-2 py-1.5 rounded-lg text-xs text-white font-mono w-52 outline-none" />
                          <button data-testid={`admin-code-generate-${a.telegram_id}`} title="Generar código seguro"
                            onClick={() => setNewCode(generateCode())}
                            className="glass-btn w-8 h-8 rounded-lg flex items-center justify-center shrink-0">
                            <Sparkles className="w-3.5 h-3.5" />
                          </button>
                          <button data-testid={`admin-code-copy-${a.telegram_id}`} title="Copiar código"
                            disabled={!newCode} onClick={() => copyCode(newCode, a.telegram_id)}
                            className="glass-btn w-8 h-8 rounded-lg flex items-center justify-center shrink-0 disabled:opacity-40">
                            {copied === a.telegram_id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                          <button data-testid={`admin-code-save-${a.telegram_id}`}
                            onClick={() => saveCode(a.telegram_id)}
                            className="glass-btn glass-btn-red px-3 py-1.5 rounded-lg text-xs">Guardar</button>
                          <button onClick={() => { setEditing(null); setNewCode(""); }}
                            className="text-white/45 text-xs hover:text-white">Cancelar</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button data-testid={`admin-code-edit-${a.telegram_id}`}
                            onClick={() => { setEditing(a.telegram_id); setNewCode(""); }}
                            className="glass-btn px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5">
                            <KeyRound className="w-3.5 h-3.5" /> {a.has_code ? "Cambiar código" : "Poner código"}
                          </button>
                          {a.has_code && (
                            <button data-testid={`admin-code-remove-${a.telegram_id}`}
                              onClick={() => removeCode(a.telegram_id)}
                              className="glass-btn px-2.5 py-1.5 rounded-lg text-xs text-red-300">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {a.telegram_id !== user?.telegram_id && (
                            <button data-testid={`revoke-btn-${a.telegram_id}`}
                              onClick={() => setUserRole(a.telegram_id, "USER")}
                              className="text-red-400 hover:text-red-300 text-xs">Revocar</button>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isSuper && (
          <div className="glass-card overflow-hidden max-w-4xl" data-testid="admin-logins-card">
            <div className="flex items-center justify-between px-4 py-3 border-b border-red-500/15">
              <div className="flex items-center gap-2 text-white font-heading font-semibold">
                <History className="w-4 h-4 text-red-400" /> Registro de accesos
                <span className="text-[11px] text-white/40 font-normal">últimos 100</span>
              </div>
              <div className="flex items-center gap-3">
                {logins.failed_24h > 0 && (
                  <span data-testid="failed-24h-badge"
                    className="flex items-center gap-1.5 text-[11px] text-amber-300">
                    <AlertTriangle className="w-3.5 h-3.5" /> {logins.failed_24h} intento(s) fallido(s) en 24 h
                  </span>
                )}
                <button data-testid="toggle-only-failed" onClick={() => setOnlyFailed((v) => !v)}
                  className={`glass-btn px-3 py-1.5 rounded-lg text-xs ${onlyFailed ? "glass-btn-red" : ""}`}>
                  {onlyFailed ? "Ver todos" : "Solo fallidos"}
                </button>
              </div>
            </div>
            <table className="w-full text-sm" data-testid="admin-logins-table">
              <thead>
                <tr className="text-left text-xs text-white/45 uppercase border-b border-red-500/15">
                  <th className="px-4 py-3">Cuándo</th>
                  <th className="px-4 py-3">Administrador</th>
                  <th className="px-4 py-3">Método</th>
                  <th className="px-4 py-3">Desde (ciudad e IP)</th>
                  <th className="px-4 py-3">Dispositivo</th>
                  <th className="px-4 py-3">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {logins.logins.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-5 text-center text-xs text-white/40"
                    data-testid="admin-logins-empty">Todavía no hay accesos registrados.</td></tr>
                )}
                {logins.logins.map((l) => (
                  <tr key={l.login_id} className="border-t border-white/5"
                    data-testid={`admin-login-row-${l.login_id}`}>
                    <td className="px-4 py-3 text-white/80 whitespace-nowrap">
                      {new Date(l.created_at).toLocaleString("es-MX",
                        { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-4 py-3 text-white/70">
                      {l.name || l.telegram_id || <span className="text-white/35">desconocido</span>}
                    </td>
                    <td className="px-4 py-3 text-white/60">
                      {METHOD_LABEL[l.method] || "Código por Telegram"}
                    </td>
                    <td className="px-4 py-3" data-testid={`login-place-${l.login_id}`}>
                      <div className="text-white/80 whitespace-nowrap">
                        {l.place || [l.city, l.country].filter(Boolean).join(", ") || "Ubicación desconocida"}
                      </div>
                      <div className="font-mono text-[11px] text-white/45">{l.ip}</div>
                    </td>
                    <td className="px-4 py-3 text-white/55">{l.device}</td>
                    <td className="px-4 py-3">
                      {l.ok
                        ? <span className="text-emerald-400 text-xs">Entró</span>
                        : <span className="text-red-400 text-xs">Falló{l.detail ? ` · ${l.detail}` : ""}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
